import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  Minus,
  Plus,
  Save,
  ShieldCheck,
  X,
  XCircle,
} from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { unwrap, unwrapArray } from '../../lib/api-envelope';
import { useAuth } from '../../contexts/AuthContext';
import { canPublishFormTemplate, canUploadSourceForm } from '../../lib/roles';
import {
  FormLibraryTemplate,
  buildCategoryOptions,
  getFormCategoryLabel,
  listFormCategories,
} from '../../lib/form-library';
import {
  ExtractionPage,
  ExtractionRun,
  FORM_FIELD_TYPES,
  FORM_FIELD_TYPE_LABELS,
  CHOICE_TYPES,
  LOW_CONFIDENCE_THRESHOLD,
  OutlineSection,
  ProposedField,
  ProposedSchema,
  SchemaError,
  buildOutline,
  fieldErrorKey,
  findDuplicateTemplate,
  formatConfidence,
  groupErrorsByField,
  isLowConfidence,
  isNonAnswerable,
  normalizeSchemaErrors,
  validateProposedSchema,
} from '../../lib/form-extraction';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Tooltip } from '../../components/ui/tooltip';
import PreuseExtractionReview from '../equipment/PreuseExtractionReview';
import { preuseTargetOf } from '../../lib/preuse-extraction';

/**
 * Screen 3 — review what Claude proposed against the scan it read, and approve
 * it into the tenant's library.
 *
 * This closes the loop opened in #343. Before it, an upload ran a two-pass
 * extraction, landed at `needs_review`, and nothing could look at it: no
 * replicated form could ever become a template.
 *
 * THREE THINGS THE PAYLOAD DICTATED, AGAINST THE ORIGINAL BRIEF
 * ------------------------------------------------------------
 *  1. There are no sections. `fields` is flat and `section_id` is never set —
 *     the outline is DERIVED by segmenting on `section_header` rows, which are
 *     themselves real fields that get written to `form_template_fields`.
 *  2. Confidence is read off the `field_confidence` COLUMN, not the copy
 *     embedded in `proposed_schema_json`. PATCH rewrites only the JSON, so the
 *     embedded copy goes stale against renamed ids the moment anyone edits.
 *  3. Category is not an extracted value being corrected. Pass 2 is never asked
 *     for one and never emits one; `approve` requires it. The reviewer supplies
 *     it, and it is persisted as a top-level key on the schema because the
 *     server preserves keys it does not recognise.
 *
 * AND ONE THING THE DATA DICTATED
 * -------------------------------
 * A proposal can arrive ALREADY FAILING VALIDATION, and both live runs of the
 * same JSA did — Pass 2 proposed `is_required: true` on a `signature`, which
 * holds no answer. The worker parks such runs at `needs_review` on purpose. So
 * the schema is validated ON LOAD and the offending row is marked before the
 * reviewer touches anything; the alternative is a mystery 422 on a form they
 * never edited. The root cause is a Pass 2 prompt that should not propose it —
 * that fix belongs in fieldiq-api, not here.
 */

/**
 * How often the page images are re-signed while the screen sits open.
 *
 * The URLs from `/pages` are presigned R2 URLs with `X-Amz-Expires=3600`, and
 * there is no proxy route for these keys — nothing else in the codebase signs
 * `page_image_keys`, so the CLAUDE.md "authenticated proxy for <img src>" rule
 * has nothing to point at here. This is the mitigation instead: hold the lease
 * for a fraction of its term and take a fresh one long before it lapses. A
 * reviewer reading a six-page form for an hour never watches a page turn into
 * a broken image.
 *
 * The URLs are never written to state or storage — they live only in the query
 * cache, which is torn down on unmount (`gcTime: 0`).
 */
const PAGE_URL_REFRESH_MS = 15 * 60 * 1000;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

/** Field types offered by the select. The closed set, in the API's order. */
const TYPE_OPTIONS = FORM_FIELD_TYPES.map((value) => ({
  value,
  label: FORM_FIELD_TYPE_LABELS[value],
}));

const INPUT_CLASS =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm ' +
  'focus:outline-none focus:border-fieldiq-accent disabled:opacity-60';

// ─── Source pages ─────────────────────────────────────────────────────────────

function SourcePages({ runId, pageCount }: { runId: string; pageCount: number | null }) {
  const [zoom, setZoom] = useState(1);
  /**
   * Pages whose image did not load. A dead `<img>` renders as a broken icon and
   * says nothing — and the whole point of this pane is that the reviewer checks
   * the schema against it. Not loading has to be legible as a failure, not as
   * an empty area they scroll past.
   */
  const [failedPages, setFailedPages] = useState<Set<number>>(new Set());

  const {
    data: pages = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['form-extraction-pages', runId],
    queryFn: async () => {
      const res = await api.forms.extractionPages(runId);
      return unwrapArray<ExtractionPage>(res.data);
    },
    // A signed URL is a lease. Never serve one from cache across a mount, never
    // keep one after unmount, and take a fresh one well inside the TTL.
    staleTime: 0,
    gcTime: 0,
    refetchInterval: PAGE_URL_REFRESH_MS,
    refetchIntervalInBackground: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const scrollToPage = (page: number) => {
    document.getElementById(`source-page-${page}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex flex-col min-h-0">
      {/* Page strip + zoom */}
      <div className="flex items-center justify-between gap-3 border-b border-fieldiq-border pb-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-widest text-fieldiq-muted">
            Source
          </span>
          {pages.map((p) => (
            <button
              key={p.page}
              onClick={() => scrollToPage(p.page)}
              className="px-2 py-1 rounded border border-fieldiq-border bg-fieldiq-card text-xs text-fieldiq-text hover:border-fieldiq-accent hover:text-fieldiq-accent transition-colors"
            >
              Page {p.page}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
            disabled={zoom <= ZOOM_MIN}
            aria-label="Zoom out"
            className="p-1.5 rounded border border-fieldiq-border text-fieldiq-text hover:border-fieldiq-accent disabled:opacity-40"
          >
            <Minus size={14} />
          </button>
          <Tooltip content="Reset zoom">
            <button
              onClick={() => setZoom(1)}
              aria-label="Reset zoom"
              className="px-2 py-1.5 rounded border border-fieldiq-border text-xs text-fieldiq-text hover:border-fieldiq-accent min-w-[3.5rem]"
            >
              {Math.round(zoom * 100)}%
            </button>
          </Tooltip>
          <button
            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
            disabled={zoom >= ZOOM_MAX}
            aria-label="Zoom in"
            className="p-1.5 rounded border border-fieldiq-border text-fieldiq-text hover:border-fieldiq-accent disabled:opacity-40"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto rounded-lg bg-fieldiq-dark/60 p-3">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-fieldiq-text py-8 justify-center">
            <Loader2 size={16} className="animate-spin text-fieldiq-accent" />
            Loading the scan…
          </div>
        )}

        {isError && !isLoading && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {getApiErrorMessage(error, 'Could not load the scanned pages.')}
          </div>
        )}

        {/* An empty pages list is a real answer, not a loading state. Say so —
            the schema on the right came FROM these pages, and a reviewer
            checking it against nothing is checking nothing. */}
        {!isLoading && !isError && pages.length === 0 && (
          <div className="text-center py-10">
            <p className="text-sm text-white font-medium">No page images for this run</p>
            <p className="text-xs text-muted-foreground mt-1">
              The source document reports{' '}
              {Number.isFinite(pageCount as number) ? pageCount : '—'} page(s), but none could be
              signed. Review against the original document instead of approving blind.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {pages.map((p) => (
            <figure key={p.page} id={`source-page-${p.page}`} className="scroll-mt-2">
              <figcaption className="text-xs text-fieldiq-muted mb-1">Page {p.page}</figcaption>
              {failedPages.has(p.page) ? (
                <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-4 text-sm text-red-300">
                  Page {p.page} could not be loaded. Its link may have expired — reload the page to
                  sign a fresh one, and do not approve against a scan you cannot see.
                </div>
              ) : (
                // Presigned R2 URL, straight from /pages on this mount. Not
                // persisted anywhere; re-signed every PAGE_URL_REFRESH_MS. The
                // key is the page number, not the URL, so a refresh swaps the
                // src in place rather than blanking the pane.
                <img
                  src={p.url}
                  alt={`Scanned source page ${p.page}`}
                  style={{ width: `${zoom * 100}%` }}
                  onLoad={() =>
                    setFailedPages((prev) => {
                      if (!prev.has(p.page)) return prev;
                      const next = new Set(prev);
                      next.delete(p.page);
                      return next;
                    })
                  }
                  onError={() => setFailedPages((prev) => new Set(prev).add(p.page))}
                  className="max-w-none rounded border border-fieldiq-border bg-white"
                />
              )}
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Choices editor ───────────────────────────────────────────────────────────

function ChoicesEditor({
  choices,
  disabled,
  invalid,
  onChange,
}: {
  choices: string[];
  disabled: boolean;
  invalid: boolean;
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-[11px] uppercase tracking-wide text-fieldiq-muted">Choices</span>
      {choices.length === 0 && (
        <p
          className={`text-xs ${invalid ? 'text-red-300' : 'text-muted-foreground'}`}
        >
          No choices yet — this field cannot be saved without at least one.
        </p>
      )}
      {choices.map((choice, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={choice}
            disabled={disabled}
            aria-label={`Choice ${i + 1}`}
            onChange={(e) => {
              const next = [...choices];
              next[i] = e.target.value;
              onChange(next);
            }}
            className={`${INPUT_CLASS} py-1.5 text-xs ${
              choice.trim() === '' ? 'border-red-500/60' : ''
            }`}
          />
          <button
            onClick={() => onChange(choices.filter((_, j) => j !== i))}
            disabled={disabled}
            aria-label={`Remove choice ${i + 1}`}
            className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40 shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...choices, ''])}
        disabled={disabled}
        className="flex items-center gap-1.5 text-xs text-fieldiq-accent hover:underline disabled:opacity-40"
      >
        <Plus size={12} />
        Add choice
      </button>
    </div>
  );
}

// ─── One field row ────────────────────────────────────────────────────────────

function FieldRow({
  field,
  index,
  errors,
  lowConfidence,
  confidenceLabel,
  readOnly,
  onPatch,
}: {
  field: ProposedField;
  index: number;
  errors: SchemaError[];
  lowConfidence: boolean;
  confidenceLabel: string;
  readOnly: boolean;
  onPatch: (index: number, patch: Partial<ProposedField>) => void;
}) {
  const errorFor = (key: string) => errors.find((e) => fieldErrorKey(e.path) === key);
  const typeError = errorFor('field_type');
  const labelError = errorFor('label');
  const requiredError = errorFor('is_required');
  const choicesError = errorFor('options.choices');
  const otherErrors = errors.filter(
    (e) => !['field_type', 'label', 'is_required', 'options.choices'].includes(fieldErrorKey(e.path)),
  );

  const type = field.field_type ?? '';
  const nonAnswerable = isNonAnswerable(type);
  const needsChoices = CHOICE_TYPES.has(type);
  const rawChoices = field.options?.choices;
  const choices = Array.isArray(rawChoices) ? rawChoices.map((c) => String(c)) : [];

  /**
   * Amber for a low score, red for a validation error, and red wins. They mean
   * different things — "the model was unsure" versus "this will be refused" —
   * and a reviewer only has one left edge to read them off.
   */
  const accent =
    errors.length > 0
      ? 'border-l-red-500'
      : lowConfidence
        ? 'border-l-fieldiq-accent'
        : 'border-l-transparent';

  return (
    <div
      data-testid={`field-row-${index}`}
      className={`border-l-2 ${accent} bg-fieldiq-card/40 rounded-r-lg px-3 py-3 space-y-2`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        {/* Label */}
        <div className="flex-1 min-w-0">
          <label className="text-[11px] uppercase tracking-wide text-fieldiq-muted mb-1 block">
            Label
          </label>
          <input
            type="text"
            value={field.label ?? ''}
            disabled={readOnly}
            aria-label={`Field ${index + 1} label`}
            onChange={(e) => onPatch(index, { label: e.target.value })}
            className={`${INPUT_CLASS} ${labelError ? 'border-red-500' : ''}`}
          />
        </div>

        {/* Type — ONLY the 14 live values. An extraction proposing anything
            else keeps its value in state and is reported below as an error the
            reviewer must resolve; it is never coerced to something renderable
            behind their back. */}
        <div className="sm:w-52 shrink-0">
          <label className="text-[11px] uppercase tracking-wide text-fieldiq-muted mb-1 block">
            Type
          </label>
          <select
            value={type}
            disabled={readOnly}
            aria-label={`Field ${index + 1} type`}
            onChange={(e) => onPatch(index, { field_type: e.target.value })}
            className={`${INPUT_CLASS} ${typeError ? 'border-red-500' : ''}`}
          >
            {/* The unsupported value, unioned in so the select can show back
                what state actually holds — see buildCategoryOptions (#345). */}
            {typeError && <option value={type}>{type || '(none)'} — unsupported</option>}
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Required */}
        <div className="sm:w-36 shrink-0">
          <label className="text-[11px] uppercase tracking-wide text-fieldiq-muted mb-1 block">
            Required
          </label>
          <label
            className={`flex items-center gap-2 h-[38px] ${
              readOnly || nonAnswerable ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            title={
              nonAnswerable
                ? `A ${type} field holds no answer, so it cannot be required — submission-time validation skips it, and the form would never submit.`
                : 'Crews must answer this before submitting'
            }
          >
            <input
              type="checkbox"
              checked={field.is_required === true}
              // Blocked here, before any request: the API refuses this at PATCH
              // and again at approve, and a 422 is a worse way to learn it.
              disabled={readOnly || nonAnswerable}
              aria-label={`Field ${index + 1} required`}
              onChange={(e) => onPatch(index, { is_required: e.target.checked })}
              className="accent-fieldiq-accent w-4 h-4 disabled:opacity-40"
            />
            <span className="text-xs text-muted-foreground">
              {nonAnswerable ? 'N/A' : 'Required'}
            </span>
          </label>

          {/* The box is disabled so nobody can MAKE a signature required — but
              a proposal that ARRIVES required must still be fixable, and both
              live extractions of this form did. Without this the reviewer is
              trapped: an error they can see, blocking Approve, with the only
              control that clears it greyed out. */}
          {nonAnswerable && field.is_required === true && !readOnly && (
            <button
              onClick={() => onPatch(index, { is_required: false })}
              className="mt-1 text-xs text-fieldiq-accent hover:underline"
            >
              Clear it
            </button>
          )}
        </div>
      </div>

      {needsChoices && (
        <ChoicesEditor
          choices={choices}
          disabled={readOnly}
          invalid={!!choicesError}
          onChange={(next) =>
            onPatch(index, { options: { ...(field.options ?? {}), choices: next } })
          }
        />
      )}

      {/* Confidence + id. The id is what a `fields[N]` error path and the
          confidence map are both keyed on, so it is worth showing. */}
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <span className="font-mono text-muted-foreground break-all">{field.id ?? '(no id)'}</span>
        {lowConfidence ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-fieldiq-accent/20 text-fieldiq-accent px-2 py-0.5 font-semibold"
            title={`The model scored this field ${confidenceLabel}, below the ${Math.round(
              LOW_CONFIDENCE_THRESHOLD * 100,
            )}% mark it was confident above elsewhere on this form. It read something here it was unsure of — check the label, the type and any choices against the scan before approving.`}
          >
            <AlertTriangle size={11} />
            Low confidence · {confidenceLabel}
          </span>
        ) : (
          <span className="text-muted-foreground">Confidence {confidenceLabel}</span>
        )}
      </div>

      {/* Inline, on the row that owns it — never a toast. */}
      {(typeError || labelError || requiredError || choicesError || otherErrors.length > 0) && (
        <ul className="space-y-1">
          {[typeError, labelError, requiredError, choicesError, ...otherErrors]
            .filter((e): e is SchemaError => !!e)
            .map((e, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-red-300">
                <XCircle size={12} className="mt-0.5 shrink-0" />
                <span className="break-words">{e.message}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

// ─── The screen ───────────────────────────────────────────────────────────────

export default function FormExtractionReviewScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { user, isLoading: authLoading } = useAuth();

  const role = user?.role;
  /**
   * Both gates wait for the role. Reading them while `user` is still null
   * answers "no" for everyone, so an admin would watch the editor render
   * disabled and the footer render empty before both flipped a beat later —
   * long enough to be reported as "the buttons are missing".
   */
  const roleResolved = !authLoading;
  /** approve AND reject are `requireRole('admin', { strict: true })`. */
  const canDispose = roleResolved && canPublishFormTemplate(role);
  /** PATCH shares its allow list with the upload/extract routes. */
  const canEdit = roleResolved && canUploadSourceForm(role);

  const [draft, setDraft] = useState<ProposedSchema | null>(null);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<SchemaError[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [notesDismissed, setNotesDismissed] = useState(false);
  const [mobilePane, setMobilePane] = useState<'source' | 'fields'>('fields');
  const [confirmingApprove, setConfirmingApprove] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const {
    data: run,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['form-extraction', id],
    queryFn: async () => {
      const res = await api.forms.extraction(id!);
      // snake_case — the raw DB row. `data: null` on a 200 is a real answer and
      // must not be read as a run.
      return unwrap<ExtractionRun>(res.data);
    },
    enabled: !!id,
  });

  /**
   * The tenant's live categories, from the same endpoint the library groups by.
   *
   * The STRICT read on purpose — `include_drafts` is admin/dispatcher only and
   * 403s for the office_staff who may legitimately be reviewing here, and it is
   * not needed: `buildCategoryOptions` unions in whatever the select is holding.
   */
  const { data: libraryTemplates = [] } = useQuery({
    queryKey: ['forms-library-categories'],
    queryFn: async () => {
      const res = await api.forms.templates();
      return unwrapArray<FormLibraryTemplate>(res.data);
    },
  });

  // Seed the editable draft once the run lands. Keyed on the run id so
  // navigating between runs re-seeds, while a background refetch of the same
  // run does not stamp on edits in progress.
  useEffect(() => {
    if (!run?.id) return;
    const schema: ProposedSchema = run.proposed_schema_json ?? { name: '', fields: [] };
    setDraft(schema);
    setBaseline(JSON.stringify(schema));
    setServerErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id]);

  const fields = useMemo(() => (Array.isArray(draft?.fields) ? draft!.fields! : []), [draft]);
  const outline = useMemo(() => buildOutline(fields), [fields]);

  /**
   * Validated on every render of the draft, INCLUDING the very first — before
   * the reviewer has touched anything. Both live extractions of this form
   * arrived failing on a required signature.
   */
  const clientErrors = useMemo(() => validateProposedSchema(draft), [draft]);

  const allErrors = useMemo(() => {
    const seen = new Set<string>();
    const merged: SchemaError[] = [];
    for (const e of [...clientErrors, ...serverErrors]) {
      const key = `${e.path}\x00${e.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(e);
    }
    return merged;
  }, [clientErrors, serverErrors]);

  const { byIndex: errorsByIndex, form: formErrors } = useMemo(
    () => groupErrorsByField(allErrors),
    [allErrors],
  );

  const name = typeof draft?.name === 'string' ? draft.name : '';
  const category = typeof draft?.category === 'string' ? draft.category : '';

  const categoryOptions = useMemo(
    () => buildCategoryOptions(listFormCategories(libraryTemplates), category),
    [libraryTemplates, category],
  );

  const duplicate = useMemo(
    () => findDuplicateTemplate(libraryTemplates, name, category),
    [libraryTemplates, name, category],
  );

  const dirty = baseline !== null && draft !== null && JSON.stringify(draft) !== baseline;
  const reviewable = run?.status === 'needs_review';

  const patchSchema = (patch: Partial<ProposedSchema>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    // The reviewer has moved; last request's complaints are no longer evidence.
    // The client mirror re-runs immediately, so nothing that is still wrong
    // disappears — only the stale server copy does.
    setServerErrors([]);
  };

  const patchField = (index: number, patch: Partial<ProposedField>) => {
    setDraft((prev) => {
      if (!prev || !Array.isArray(prev.fields)) return prev;
      const next = prev.fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
      return { ...prev, fields: next };
    });
    setServerErrors([]);
  };

  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /**
   * Save the whole document back. PATCH is a full replacement, so `draft` is
   * sent entire — including `notes` and the embedded `field_confidence`, which
   * nothing here authors and the server preserves.
   */
  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.forms.updateExtraction(id!, draft);
      return unwrap<{ id: string; proposed_schema_json: ProposedSchema }>(res.data);
    },
    onSuccess: (saved) => {
      setServerErrors([]);
      // The response carries the SANITIZED schema — the server strips unknown
      // `maps_to` bindings. Adopting it keeps the screen showing what is
      // actually stored rather than what we hoped was.
      if (saved?.proposed_schema_json) {
        setDraft(saved.proposed_schema_json);
        setBaseline(JSON.stringify(saved.proposed_schema_json));
      } else if (draft) {
        setBaseline(JSON.stringify(draft));
      }
      queryClient.invalidateQueries({ queryKey: ['form-extraction', id] });
      toast.success('Draft saved');
    },
    onError: (e: any) => {
      const structured = normalizeSchemaErrors(e);
      setServerErrors(structured);
      toast.error(
        structured.length > 0
          ? `${structured.length} ${structured.length === 1 ? 'field needs' : 'fields need'} fixing before this can be saved`
          : getApiErrorMessage(e, 'Could not save these edits'),
      );
    },
  });

  /**
   * Approve, publishing outright.
   *
   * `publish: true` is stated, not inherited: the server defaults it to FALSE,
   * which lands the template in `draft` — filtered out of the library read, and
   * rescuable only by a second act on another screen that nobody found. Six
   * tenant clones sat that way for months.
   *
   * Unsaved edits are PATCHed FIRST. Approve reads the STORED schema, so
   * approving straight from a dirty screen would publish a form the reviewer
   * can see they changed and the crew would never receive — the silent
   * wrong-value failure, with a safety document on the end of it.
   */
  const approveMutation = useMutation({
    mutationFn: async () => {
      if (dirty) await api.forms.updateExtraction(id!, draft);
      const res = await api.forms.approveExtraction(id!, {
        name: name.trim(),
        category,
        publish: true,
      });
      return unwrap<{ templateId: string; status: string; published: boolean }>(res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms-library'] });
      queryClient.invalidateQueries({ queryKey: ['forms-library-categories'] });
      queryClient.invalidateQueries({ queryKey: ['form-extractions-pending'] });
      setConfirmingApprove(false);
      toast.success(`"${name.trim()}" is live in your ${getFormCategoryLabel(category)} library`);
      navigate('/settings/safety/forms', { replace: true });
    },
    onError: (e: any) => {
      const structured = normalizeSchemaErrors(e);
      if (structured.length > 0) setServerErrors(structured);
      setConfirmingApprove(false);
      toast.error(getApiErrorMessage(e, 'Could not approve this form'));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.forms.rejectExtraction(id!, rejectReason.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-extractions-pending'] });
      setRejecting(false);
      toast.success('Extraction rejected — no form was created');
      navigate('/settings/safety/forms', { replace: true });
    },
    onError: (e: any) => toast.error(getApiErrorMessage(e, 'Could not reject this extraction')),
  });

  const busy = saveMutation.isPending || approveMutation.isPending || rejectMutation.isPending;
  const blockingErrors = allErrors.length > 0;
  const canApprove = reviewable && !blockingErrors && !!name.trim() && !!category && !busy;

  // ── Render ──────────────────────────────────────────────────────────────────

  const pageTitle = run?.original_filename || 'Review Extracted Form';

  // A run started for a PRE-USE template (api#1210) carries `preuse_target`; an
  // ordinary run has no such key. Its proposal is a checklist, not a form
  // schema, so it gets its own editor. Placed after every hook above so the
  // hook order is the same on both paths; nothing above writes anything.
  if (run && preuseTargetOf(run)) {
    return (
      <PreuseExtractionReview
        run={run}
        role={role}
        roleResolved={roleResolved}
        sourcePane={<SourcePages runId={run.id} pageCount={run.page_count} />}
      />
    );
  }

  return (
    <div className="min-h-full bg-background pb-[calc(var(--bottom-chrome)+2rem)]">
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/settings/safety/forms')}
              aria-label="Back"
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-foreground truncate">Review Extracted Form</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden sm:inline text-xs text-muted-foreground truncate max-w-[16rem]">
              {pageTitle}
            </span>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-fieldiq-text">
            <Loader2 size={16} className="animate-spin text-fieldiq-accent" />
            Loading the extraction…
          </div>
        )}

        {isError && !isLoading && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {getApiErrorMessage(error, 'Could not load this extraction run.')}
          </div>
        )}

        {!isLoading && !isError && !run && (
          <div className="rounded-lg border border-fieldiq-border bg-fieldiq-card/40 px-4 py-3 text-sm text-fieldiq-text">
            This extraction run could not be found.
          </div>
        )}

        {run && (
          <>
            {/* Not `needs_review` — say what happened rather than presenting an
                editor whose every write answers 409. */}
            {!reviewable && (
              <div className="rounded-lg border border-fieldiq-accent/40 bg-fieldiq-accent/10 px-4 py-3">
                <p className="text-sm text-fieldiq-accent font-semibold">
                  This run is {run.status} — it is no longer open for review.
                </p>
                {run.error_message && (
                  <p className="text-sm text-fieldiq-text mt-1 break-words">{run.error_message}</p>
                )}
                {run.created_template_id && (
                  <button
                    onClick={() => navigate('/settings/safety/forms')}
                    className="mt-2 text-sm text-fieldiq-accent underline"
                  >
                    See it in the library
                  </button>
                )}
              </div>
            )}

            {/* What the model was unsure about, in its own words. */}
            {run.ai_notes && !notesDismissed && (
              <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-3">
                <div className="flex items-start gap-2">
                  <Info size={16} className="text-sky-300 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-sky-200">What the model flagged</p>
                    <p className="text-sm text-sky-100/90 mt-1 whitespace-pre-line break-words">
                      {run.ai_notes}
                    </p>
                  </div>
                  <button
                    onClick={() => setNotesDismissed(true)}
                    aria-label="Dismiss notes"
                    className="p-1 rounded text-sky-300/70 hover:text-sky-200 hover:bg-white/10 shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Mobile pane switch. The split does not survive a phone width, so
                it becomes two tabs rather than two half-legible columns. */}
            <div className="flex lg:hidden rounded-lg border border-fieldiq-border overflow-hidden">
              {(['source', 'fields'] as const).map((pane) => (
                <button
                  key={pane}
                  onClick={() => setMobilePane(pane)}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    mobilePane === pane
                      ? 'bg-fieldiq-accent text-white'
                      : 'bg-fieldiq-card text-fieldiq-text'
                  }`}
                >
                  {pane === 'source' ? 'Source scan' : `Fields (${fields.length})`}
                </button>
              ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-6 items-start">
              {/* LEFT — the scan */}
              {/* `flex`/`hidden` rather than a `lg:block` + `lg:flex` pair —
                  both would apply at lg and the winner would be whichever the
                  stylesheet emitted last. */}
              {/* `data-testid` so the pane-switch test can assert on THIS
                  element's classes. Tailwind is not loaded in jsdom, so
                  `hidden` has no computed effect and `toBeVisible()` passes in
                  both states — the class is the only observable signal. */}
              <div
                data-testid="source-pane"
                className={`w-full lg:w-[42%] shrink-0 flex-col ${
                  mobilePane === 'source' ? 'flex' : 'hidden'
                } lg:flex lg:sticky lg:top-2 lg:max-h-[calc(100vh-13rem)]`}
              >
                <SourcePages runId={run.id} pageCount={run.page_count} />
              </div>

              {/* RIGHT — the proposed schema */}
              <div className="w-full min-w-0 flex-1">
                <div
                  data-testid="schema-pane"
                  className={mobilePane === 'fields' ? '' : 'hidden lg:block'}
                >
                  {/* Name + category. The name is how a crew tells three JSAs
                      apart, so it leads. */}
                  <div className="rounded-lg border border-fieldiq-border bg-fieldiq-card/40 p-4 space-y-3">
                    <div>
                      <label
                        htmlFor="extraction-name"
                        className="text-xs text-fieldiq-text mb-1 block"
                      >
                        Form name *
                      </label>
                      <input
                        id="extraction-name"
                        type="text"
                        value={name}
                        disabled={!canEdit || !reviewable}
                        placeholder="e.g. Thread Wash JSA"
                        onChange={(e) => patchSchema({ name: e.target.value })}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-lg font-semibold focus:outline-none focus:border-fieldiq-accent disabled:opacity-60"
                      />
                      {!name.trim() && (
                        <p className="text-xs text-red-300 mt-1">A form name is required.</p>
                      )}
                      {duplicate && (
                        <p className="flex items-start gap-1.5 text-xs text-fieldiq-accent mt-1.5">
                          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                          <span>
                            "{duplicate.name}" is already live in{' '}
                            {getFormCategoryLabel(category)}. Two forms with one name is how a crew
                            fills in the wrong one — rename this unless you mean to run both.
                          </span>
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="extraction-category"
                        className="text-xs text-fieldiq-text mb-1 block"
                      >
                        Category *
                      </label>
                      <select
                        id="extraction-category"
                        value={category}
                        disabled={!canEdit || !reviewable}
                        onChange={(e) => patchSchema({ category: e.target.value })}
                        className={INPUT_CLASS}
                      >
                        {/* No category is a real state — the model is never
                            asked for one. An empty placeholder beats
                            pre-selecting a category nobody chose. */}
                        <option value="">Choose a category…</option>
                        {categoryOptions.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      {!category && (
                        <p className="text-xs text-muted-foreground mt-1">
                          The extraction does not propose one — it is how crews find this form.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Document-level problems. These block Approve and belong to
                      no row, so they cannot be left to the rows to show. */}
                  {formErrors.length > 0 && (
                    <ul className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 space-y-1">
                      {formErrors.map((e, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm text-red-300">
                          <XCircle size={13} className="mt-0.5 shrink-0" />
                          <span className="break-words">
                            {e.path ? <span className="font-mono text-xs">{e.path}: </span> : null}
                            {e.message}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Counts, so a reviewer knows what they are looking at. */}
                  <div className="flex items-center gap-4 flex-wrap text-xs text-muted-foreground mt-4">
                    <span>{fields.length} fields</span>
                    <span>
                      {outline.filter((s) => s.header).length} sections
                    </span>
                    {blockingErrors && (
                      <span className="text-red-300 font-semibold">
                        {allErrors.length} {allErrors.length === 1 ? 'problem' : 'problems'} to
                        resolve
                      </span>
                    )}
                  </div>

                  {/* Outline */}
                  <div className="space-y-3 mt-3">
                    {outline.map((section) => (
                      <SectionBlock
                        key={section.key}
                        section={section}
                        collapsed={collapsed.has(section.key)}
                        onToggle={() => toggleSection(section.key)}
                        errorsByIndex={errorsByIndex}
                        confidence={run.field_confidence}
                        readOnly={!canEdit || !reviewable}
                        onPatchField={patchField}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer actions */}
      {/* Sticks above the fixed bottom nav, which covers the last
          --bottom-chrome of <main> below md. At bottom 0 these buttons sat
          under the bar at phone width (fe#839). md and up have no bar. */}
      {run && reviewable && roleResolved && (canEdit || canDispose) && (
        <div data-testid="extraction-review-actions" className="sticky bottom-[var(--bottom-chrome)] md:bottom-0 border-t border-fieldiq-border bg-fieldiq-card/95 backdrop-blur px-6 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-fieldiq-text">
              {blockingErrors ? (
                <span className="text-red-300">
                  Resolve {allErrors.length}{' '}
                  {allErrors.length === 1 ? 'problem' : 'problems'} before publishing
                </span>
              ) : dirty ? (
                'Unsaved changes'
              ) : (
                'No unsaved changes'
              )}
            </p>

            <div className="flex items-center gap-2 flex-wrap">
              {/* approve and reject are both admin-strict server-side. An
                  office_staff reviewer edits and saves; they do not dispose. */}
              {canDispose && (
                <button
                  onClick={() => setRejecting(true)}
                  disabled={busy}
                  className="px-4 py-2 bg-fieldiq-border text-white rounded-lg text-sm font-medium hover:bg-red-500/20 hover:text-red-300 transition-colors disabled:opacity-50"
                >
                  Reject
                </button>
              )}

              {canEdit && (
                <Tooltip content={dirty ? 'Save these edits without publishing' : 'Nothing to save'}>
                  <button
                    onClick={() => saveMutation.mutate()}
                    disabled={busy || !dirty}
                    className="flex items-center gap-2 px-4 py-2 bg-fieldiq-card border border-fieldiq-border text-white rounded-lg text-sm font-medium hover:border-fieldiq-accent transition-colors disabled:opacity-50"
                  >
                    {saveMutation.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Save size={16} />
                    )}
                    Save Draft
                  </button>
                </Tooltip>
              )}

              {canDispose && (
                <Tooltip content={
                    blockingErrors
                      ? 'Resolve the highlighted problems first'
                      : !name.trim()
                        ? 'Give this form a name first'
                        : !category
                          ? 'Choose a category first'
                          : 'Publish this form to your crews'
                  }>
                  <button
                    onClick={() => setConfirmingApprove(true)}
                    disabled={!canApprove}
                    className="flex items-center gap-2 px-4 py-2 bg-fieldiq-accent text-white rounded-lg text-sm font-semibold hover:bg-fieldiq-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {approveMutation.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <ShieldCheck size={16} />
                    )}
                    Approve &amp; Publish
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmingApprove}
        title="Publish this form?"
        confirmLabel="Approve & Publish"
        confirmVariant="primary"
        loading={approveMutation.isPending}
        onCancel={() => setConfirmingApprove(false)}
        onConfirm={() => approveMutation.mutate()}
        body={
          <div className="space-y-2">
            <p>
              <span className="text-fieldiq-muted">Name</span>
              <br />
              <span className="text-white font-semibold break-words">{name.trim() || '—'}</span>
            </p>
            <p>
              <span className="text-fieldiq-muted">Category</span>
              <br />
              <span className="text-white font-semibold">
                {category ? getFormCategoryLabel(category) : '—'}
              </span>
            </p>
            <p className="text-xs text-gray-400">
              {fields.length} fields will be created. This publishes immediately — your crews will
              see it in their Forms list.
              {dirty && ' Your unsaved edits are saved first.'}
            </p>
            {duplicate && (
              <p className="text-xs text-fieldiq-accent">
                Note: "{duplicate.name}" already exists in this category.
              </p>
            )}
          </div>
        }
      />

      <Dialog open={rejecting} onOpenChange={() => !rejectMutation.isPending && setRejecting(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject this extraction</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fieldiq-text">
            No form is created and nothing reaches your crews. The reason is stored on the run, so
            whoever uploaded it can see why.
          </p>
          <label htmlFor="reject-reason" className="text-xs text-fieldiq-text mt-3 block">
            Reason
          </label>
          <textarea
            id="reject-reason"
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. The scan was too faint to read the hazard table"
            className={`${INPUT_CLASS} resize-none mt-1`}
          />
          <DialogFooter>
            <button
              onClick={() => setRejecting(false)}
              disabled={rejectMutation.isPending}
              className="px-4 py-2 bg-fieldiq-border text-white rounded-lg text-sm font-medium hover:bg-fieldiq-border/80 disabled:opacity-50"
            >
              Cancel
            </button>
            <Tooltip content={!rejectReason.trim() ? 'Give a reason before rejecting' : undefined}>
              <button
                onClick={() => rejectMutation.mutate()}
                disabled={rejectMutation.isPending || !rejectReason.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
              </button>
            </Tooltip>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── One derived section ──────────────────────────────────────────────────────

/**
 * A section is a display grouping over a FLAT list — collapsing it hides its
 * children and nothing else.
 *
 * The `section_header` itself is a real field that gets written to
 * `form_template_fields`, so it stays visible and editable while collapsed, and
 * neither collapsing nor expanding touches `fields` or the indices the error
 * paths are addressed to.
 */
function SectionBlock({
  section,
  collapsed,
  onToggle,
  errorsByIndex,
  confidence,
  readOnly,
  onPatchField,
}: {
  section: OutlineSection;
  collapsed: boolean;
  onToggle: () => void;
  errorsByIndex: Map<number, SchemaError[]>;
  confidence: Record<string, number> | null;
  readOnly: boolean;
  onPatchField: (index: number, patch: Partial<ProposedField>) => void;
}) {
  const indices = [
    ...(section.headerIndex !== null ? [section.headerIndex] : []),
    ...section.entries.map((e) => e.index),
  ];
  // Surfaced on the header so a collapsed section cannot hide a blocker.
  const errorCount = indices.reduce((n, i) => n + (errorsByIndex.get(i)?.length ?? 0), 0);

  return (
    <section className="rounded-lg border border-fieldiq-border overflow-hidden">
      <button
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="w-full flex items-center gap-2 px-3 py-2 bg-fieldiq-card hover:bg-fieldiq-card/70 transition-colors text-left"
      >
        {collapsed ? (
          <ChevronRight size={16} className="text-fieldiq-muted shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-fieldiq-muted shrink-0" />
        )}
        <span className="text-sm font-bold uppercase tracking-widest text-fieldiq-muted truncate">
          {section.title}
        </span>
        <span className="ml-auto flex items-center gap-2 shrink-0">
          {errorCount > 0 && (
            <span className="rounded-full bg-red-500/20 text-red-300 px-2 py-0.5 text-[11px] font-semibold">
              {errorCount}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {section.entries.length} {section.entries.length === 1 ? 'field' : 'fields'}
          </span>
        </span>
      </button>

      {!collapsed && (
        <div className="p-3 space-y-2 bg-background">
          {/* The header row itself — editable like any other field, because it
              is one. Renaming it renames the printed heading on the form. */}
          {section.header && section.headerIndex !== null && (
            <FieldRow
              field={section.header}
              index={section.headerIndex}
              errors={errorsByIndex.get(section.headerIndex) ?? []}
              lowConfidence={isLowConfidence(confidence, section.header.id)}
              confidenceLabel={formatConfidence(confidence, section.header.id)}
              readOnly={readOnly}
              onPatch={onPatchField}
            />
          )}

          {section.entries.map(({ field, index }) => (
            <FieldRow
              key={index}
              field={field}
              index={index}
              errors={errorsByIndex.get(index) ?? []}
              lowConfidence={isLowConfidence(confidence, field.id)}
              confidenceLabel={formatConfidence(confidence, field.id)}
              readOnly={readOnly}
              onPatch={onPatchField}
            />
          ))}

          {section.entries.length === 0 && !section.header && (
            <p className="text-xs text-muted-foreground px-1">No fields in this section.</p>
          )}
        </div>
      )}
    </section>
  );
}
